import { EIDPattern } from "constants/scanner";

import { EIDOutputFormat } from "./types";

const HEX_EID_PATTERN = /^[A-Fa-f0-9]{16}$/;
const ISO_TIMESTAMP_PATTERN =
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z?/;
const HEX_EID_COUNTRY_SHIFT_BITS = BigInt(38);
const HEX_EID_COUNTRY_MASK = BigInt("0x3FF");
const HEX_EID_ANIMAL_MASK = BigInt("0x3FFFFFFFFF");

/**
 * Converts the scanner's 16-digit hexadecimal EID into the spaced decimal form used by the app.
 * The conversion keeps the country code and animal ID readable even when the device emits compact hex.
 */
function convertHexEidToAscii(hex: string): string {
  const long = BigInt(`0x${hex}`);
  const country = (long >> HEX_EID_COUNTRY_SHIFT_BITS) & HEX_EID_COUNTRY_MASK;
  const animal = long & HEX_EID_ANIMAL_MASK;
  return `${country.toString().padStart(3, "0")} ${animal.toString().padStart(12, "0")}`;
}

/**
 * Reads the scanner's reported EID format so later payload parsing uses the same encoding.
 * The device may expose spaced decimal, compact decimal, or hexadecimal output depending on its configuration.
 */
export function tryParseEidOutputFormat(
  payload: string,
): EIDOutputFormat | undefined {
  const raw = payload.trim().replace(/^\[/, "").replace(/]$/, "").trim();

  if (
    raw === EIDOutputFormat.WITH_SPACE ||
    raw === EIDOutputFormat.NO_SPACE ||
    raw === EIDOutputFormat.HEX
  ) {
    return raw;
  }

  return undefined;
}

/**
 * Normalises a raw payload into the app's spaced EID format when the scanner's output format is known.
 * When the format is unknown, the helper falls back across the supported encodings so live scans still import cleanly.
 */
export function normaliseEid(
  payload: string,
  outputFormat: EIDOutputFormat | undefined,
): string | null {
  const candidate = payload.trim();
  if (!candidate) {
    return null;
  }

  const parseWithSpace = (): string | null => {
    const match = EIDPattern.SIXTEEN_DIGIT_PATTERN.exec(candidate);
    return match ? match[1] : null;
  };

  const parseNoSpace = (): string | null =>
    /^\d{15}$/.test(candidate)
      ? `${candidate.slice(0, 3)} ${candidate.slice(3)}`
      : null;

  const parseHex = (): string | null =>
    HEX_EID_PATTERN.test(candidate) ? convertHexEidToAscii(candidate) : null;

  if (outputFormat === EIDOutputFormat.WITH_SPACE) {
    return parseWithSpace();
  }

  if (outputFormat === EIDOutputFormat.NO_SPACE) {
    return parseNoSpace();
  }

  if (outputFormat === EIDOutputFormat.HEX) {
    return parseHex();
  }

  return parseWithSpace() ?? parseNoSpace() ?? parseHex();
}

/**
 * Extracts an EID from a saved-session record, whether the scanner stored it directly or inside a comma-separated row.
 * The saved-scan format can bundle metadata and scan content together, so the helper checks the full record and then each token.
 */
export function extractEidFromRecord(
  record: string,
  outputFormat: EIDOutputFormat | undefined,
): string | null {
  const directMatch = normaliseEid(record, outputFormat);
  if (directMatch) {
    return directMatch;
  }

  for (const part of record
    .split(",")
    .map(token => token.trim())
    .filter(Boolean)) {
    const tokenMatch = normaliseEid(part, outputFormat);
    if (tokenMatch) {
      return tokenMatch;
    }
  }

  return null;
}

/**
 * Extracts the timestamp from a saved-session record and normalises it to ISO-8601.
 * When the record does not contain a timestamp, the current time is used so the imported scan still has a creation value.
 */
export function extractScanTime(record: string): string {
  const match = ISO_TIMESTAMP_PATTERN.exec(record);
  return match ? new Date(match[0]).toISOString() : new Date().toISOString();
}
